@if (product(); as p) {
  <div class="flex flex-col gap-4">
    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <i class="pi pi-info-circle text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Información general</span>
      </div>
      <div class="p-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Nombre</p>
            <p class="text-sm font-semibold text-slate-900">{{ p.name || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Estado</p>
            <p-tag
              severity="secondary"
              class="text-xs"
              [value]="p.state === 'ACTIVE' ? 'Activo' : 'Inactivo'" />
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">SKU</p>
            <p class="text-sm text-slate-700 font-mono">{{ p.sku || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">ID externo</p>
            <p class="text-sm text-slate-700 font-mono">{{ p.externalId || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Precio unitario</p>
            <p class="text-sm font-semibold text-slate-900 tabular-nums">
              {{ (p.unitPrice ?? 0) | currency:(p.currency?.code || 'MXN'):'symbol':'1.2-2' }}
            </p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Moneda</p>
            <p class="text-sm text-slate-700">{{ p.currency?.name || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Impuesto incluido</p>
            <p-tag
              severity="secondary"
              class="text-xs"
              [value]="p.taxInclusive ? 'Sí' : 'No'" />
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Fecha de creación</p>
            <p class="text-sm text-slate-700">{{ p.createdAt ? (p.createdAt | date:'dd/MM/yyyy') : '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Última actualización</p>
            <p class="text-sm text-slate-700">{{ p.updatedAt ? (p.updatedAt | date:'dd/MM/yyyy') : '—' }}</p>
          </div>
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Descripción</p>
            <p class="text-sm text-slate-700 whitespace-pre-wrap">{{ p.description || '—' }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
          <i class="pi pi-link text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Datos fiscales</span>
      </div>
      <div class="divide-y divide-slate-100">
        <div class="flex items-start gap-2 px-4 py-3">
          <div class="min-w-0 flex-1">
            <span class="text-[11px] font-semibold text-slate-600 uppercase">Unidad de medida</span>
            <p class="text-xs text-slate-900 leading-snug mt-0.5">
              {{ p.measureUnitName || p.measureUnitCode || '—' }}
            </p>
            @if (p.measureUnitId) {
              <p class="text-[11px] text-slate-500 mt-0.5">ID: {{ p.measureUnitId }}</p>
            }
          </div>
        </div>
        <div class="flex items-start gap-2 px-4 py-3">
          <div class="min-w-0 flex-1">
            <span class="text-[11px] font-semibold text-slate-600 uppercase">Código fiscal</span>
            <p class="text-xs text-slate-900 leading-snug mt-0.5">
              {{ p.taxCodeTaxCode || '—' }}
            </p>
            @if (p.taxCodeDescription) {
              <p class="text-[11px] text-slate-500 mt-0.5">{{ p.taxCodeDescription }}</p>
            }
            @if (p.taxCodeId) {
              <p class="text-[11px] text-slate-500 mt-0.5">ID: {{ p.taxCodeId }}</p>
            }
          </div>
        </div>
      </div>
    </div>

    @if (p.taxes?.length) {
      <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
          <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
            <i class="pi pi-percentage text-sm"></i>
          </span>
          <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Impuestos del catálogo ({{ p.taxes.length }})</span>
        </div>
        <div class="divide-y divide-slate-100">
          @for (productTax of p.taxes; track productTax.id) {
            <div class="flex items-start gap-2 px-4 py-2">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1.5 min-w-0 flex-wrap">
                  <span class="text-xs font-medium text-slate-900 truncate">
                    {{ productTax.tax?.displayName || productTax.tax?.taxKey || '—' }}
                  </span>
                  @if (productTax.taxRate != null) {
                    <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                      {{ productTax.taxRate }}%
                    </span>
                  }
                </div>
                <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                  @if (applicationTypeLabel(productTax.applicationType); as applicationLabel) {
                    <span class="text-xs text-slate-500">{{ applicationLabel }}</span>
                  }
                  @if (collectionTypeLabel(productTax.collectionType); as collectionLabel) {
                    <span class="text-xs text-slate-500">{{ collectionLabel }}</span>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    }
  </div>
}
